import axios from "axios";
import { Token } from "../utils/localStorage.util";
import config from "../config.js";

/** Evita baseURL relativa quando o .env tem typo `KEY==http://...` (valor vira `=http://...`). */
function normalizarBaseUrlPisCofins(url) {
  if (!url || typeof url !== "string") return url;
  let u = url.trim();
  while (u.startsWith("=")) u = u.slice(1).trim();
  return u;
}

const baseUrlPisCofins = normalizarBaseUrlPisCofins(config.REACT_APP_HOST_API_PISCOFINS);

if (!baseUrlPisCofins) {
  console.error('❌ ERRO: REACT_APP_HOST_API_PISCOFINS não está configurada. Configure a variável de ambiente antes de executar a aplicação.');
  throw new Error('REACT_APP_HOST_API_PISCOFINS não está configurada. Verifique as variáveis de ambiente.');
}

const apiPiscofins = axios.create({
  baseURL: baseUrlPisCofins,
});

apiPiscofins.interceptors.request.use(
  async (config) => {
    const token = Token.getToken();
    if (token) {
      config.headers.Authorization = token;
    }

    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

apiPiscofins.interceptors.response.use(
  (response) => {
    return response.data;
  },

  async function (error) {
    return Promise.reject(error);
  }
);

export function buscarPreviasPisCofins() {
  return apiPiscofins.get(`/previas/pis-cofins`);
}

export function buscarPreviasPisCofinsPorTexto(texto) {
  return apiPiscofins.get(`/previas/pis-cofins/${texto}`);
}

export function editarPreviasPisCofins(id, body) {
  return apiPiscofins.put(`/previas/pis-cofins/${id}`, body);
}

export function criarNovaPreviaPisCofins(body) {
  return apiPiscofins.post(`/previas/pis-cofins`, body);
}

export function liberarVersaoPrevia(idPrevia) {
  return apiPiscofins.put(`/previas/pis-cofins/${idPrevia}/liberar`);
}

export function aprovarProjetoPisCofins(idPrevia, { versaoId }) {
  return apiPiscofins.put(`/previas/pis-cofins/${idPrevia}/aprovar`, { versaoId });
}

/**
 * Reenvia à Conta Corrente os créditos de um projeto já aprovado (mesmos ids, sem duplicar).
 * Retorna { sucesso, creditos: { total, enviados, pendentes } }.
 */
export function reenviarCreditosPisCofins(idPrevia, { versaoId }) {
  return apiPiscofins.post(`/previas/pis-cofins/${idPrevia}/reenviar-creditos`, { versaoId });
}

export function buscarPreviaVersaoPisCofins(idVersao) {
  return apiPiscofins.get(`/previas/pis-cofins/versao/${idVersao}`);
}

export function listarVersoesPreviaPisCofins(idPrevia) {
  return apiPiscofins.get(`/previas/pis-cofins/${idPrevia}/versoes`);
}

/**
 * Exporta CSV de lançamentos PIS/COFINS da versão (apenas compoe_projeto = true).
 * Arquivo salvo no servidor em Relatórios. Retorna { nomeArquivo } ou { nomeArquivo: null, mensagem }.
 */
export function exportarCsvPreviaPisCofins(idPreviaVersao) {
  return apiPiscofins.post(`/previas/pis-cofins/versao/${idPreviaVersao}/exportar-csv`);
}

/** Reaplica SELIC nos lançamentos da prévia e recalcula totais compõe (todas as versões). */
export function atualizarSelicPreviaPisCofins(idPrevia) {
  return apiPiscofins.post(`/conciliacao_sped/previa/${idPrevia}/atualizar-selic`);
}

// ==========================
// MOTORES - Endpoints para Sob Despesas
// ==========================

/**
 * Busca contas com totais da conciliação SPED por prévia (tela Sob Despesa).
 * Com versaoId: totais e grupos consideram apenas lançamentos com "compõe projeto" (tabela de resumo).
 * Filtros opcionais: filtroDescricao (descrição lançamento), filtroColuna (descricaoConta | cnpj), filtroValor.
 * @param {number} previaId - ID da prévia PIS/COFINS
 * @param {number} [versaoId] - ID da versão (opcional)
 * @param {string} [filtroDescricao] - Filtro por descrição do lançamento
 * @param {string} [filtroColuna] - descricaoConta | cnpj | codigoEcd | tipoContaContabil (contas_contabeis.tipo) | tipoEcd (alias) | compoeProjeto
 * @param {string|number|boolean|null} [filtroValor] - Valor do filtro (string vazio em tipoContaContabil/tipoEcd = só contas sem tipo no plano)
 * @param {number} [pagina=1] - Página atual
 * @param {number} [limite=10] - Itens por página
 * @returns {Promise<Object>} { sucesso, previaId, grupos, totais, paginacao, empresa, cnpjEmpresa, periodo }
 */
export function buscarConciliacaoSpedContas(previaId, versaoId = undefined, filtroDescricao = undefined, filtroColuna = undefined, filtroValor = undefined) {
  const params = new URLSearchParams();
  if (versaoId != null && Number.isInteger(versaoId) && versaoId >= 1) {
    params.append('versaoId', String(versaoId));
  }
  if (filtroDescricao && String(filtroDescricao).trim()) {
    params.append('filtroDescricao', String(filtroDescricao).trim());
  }
  const colunaTipoPlano = filtroColuna === 'tipoContaContabil' || filtroColuna === 'tipoEcd';
  const permiteFiltroTipoVazio =
    colunaTipoPlano &&
    filtroValor !== undefined &&
    filtroValor !== null &&
    String(filtroValor).trim() === '';
  if (
    filtroColuna &&
    filtroValor !== undefined &&
    filtroValor !== null &&
    (String(filtroValor).trim() !== '' || permiteFiltroTipoVazio)
  ) {
    params.append('filtroColuna', filtroColuna);
    if (typeof filtroValor === 'boolean') {
      params.append('filtroValor', filtroValor ? 'true' : 'false');
    } else if (filtroValor === null) {
      params.append('filtroValor', 'null');
    } else {
      params.append('filtroValor', String(filtroValor));
    }
  }
  const query = params.toString();
  return apiPiscofins.get(`/conciliacao_sped/${previaId}/contas?${query}`);
}

/**
 * Busca totais "compõe projeto" (ECD, PIS, COFINS) da conciliação SPED para uma versão (resumo geral).
 * @param {number} versaoId - ID da versão
 * @returns {Promise<Object>} { sucesso, totais: { valorTotalEcd, pis, cofins, totalLinhas } }
 */
export function buscarTotaisCompoeConciliacaoSped(versaoId) {
  return apiPiscofins.get(`/versoes/${versaoId}/conciliacao_sped/totais`);
}

/**
 * Busca lançamentos da conciliação SPED por prévia, schema e id da conta contábil (drill down), paginados.
 * Filtra por schema + previaId + idContaContabil para que cada conta receba apenas seus lançamentos.
 * @param {number} previaId - ID da prévia PIS/COFINS
 * @param {number} idContaContabil - ID da conta contábil (contas_contabeis.id)
 * @param {number} versaoId - ID da versão (para compoe/base legal)
 * @param {number} pagina - Página (default 1)
 * @param {number} limite - Itens por página (default 10)
 * @param {string} schemaEmpresa - Schema da empresa (ex: z_20352049000110). Obrigatório.
 * @returns {Promise<Object>} { sucesso, linhas, paginacao: { pagina, limite, total, totalPaginas } }
 */
export function buscarConciliacaoSpedLancamentos(previaId, idContaContabil, versaoId, pagina = 1, limite = 10, schemaEmpresa = undefined) {
  const params = new URLSearchParams();
  params.append('idContaContabil', String(idContaContabil));
  params.append('versaoId', String(versaoId));
  params.append('pagina', String(pagina));
  params.append('limite', String(limite));
  if (schemaEmpresa != null && String(schemaEmpresa).trim() !== '') {
    params.append('schemaEmpresa', String(schemaEmpresa).trim());
  }
  return apiPiscofins.get(`/conciliacao_sped/${previaId}/lancamentos?${params.toString()}`);
}

/**
 * Busca dados agrupados do motor por conta contábil (legado; preferir buscarConciliacaoSpedContas para Sob Despesa)
 * @param {number} versaoId - ID da versão
 * @param {string} script - Script do motor (ex: CONCILIACAO_SPED)
 * @param {string} filtroDescricao - Filtro opcional por descrição de lançamento
 * @param {string} filtroColuna - Filtro opcional por coluna (descricaoConta, cnpj, dtLancamento)
 * @param {string|number|boolean|null} filtroValor - Valor do filtro opcional
 * @returns {Promise<Object>} Dados agrupados
 */
export function buscarMotorAgrupado(versaoId, script, filtroDescricao = undefined, filtroColuna = undefined, filtroValor = undefined) {
  const queryParams = new URLSearchParams();
  if (filtroDescricao && filtroDescricao.trim()) {
    queryParams.append('filtroDescricao', filtroDescricao.trim());
  }
  if (filtroColuna && filtroValor !== undefined && filtroValor !== null && filtroValor !== '') {
    queryParams.append('filtroColuna', filtroColuna);
    // Converter valor para string apropriadamente
    if (typeof filtroValor === 'boolean') {
      queryParams.append('filtroValor', filtroValor ? 'true' : 'false');
    } else if (filtroValor === null) {
      queryParams.append('filtroValor', 'null');
    } else {
      queryParams.append('filtroValor', String(filtroValor));
    }
  }
  
  const queryString = queryParams.toString();
  const url = `/versoes/${versaoId}/motores/${script}/agrupado${queryString ? `?${queryString}` : ''}`;
  
  return apiPiscofins.get(url);
}

/**
 * Busca contas contábeis da prévia para o motor (contas que têm lançamentos)
 * @param {number} versaoId - ID da versão
 * @param {string} script - Script do motor (ex: CONCILIACAO_SPED)
 * @returns {Promise<Object>} { sucesso, versaoId, script, contas: [{ id, codigo_contrib, descricao_contrib, codigo_ecd, descricao_ecd, contaContabil }] }
 */
export function buscarMotorContas(versaoId, script) {
  return apiPiscofins.get(
    `/versoes/${versaoId}/motores/${script}/contas`
  );
}

/**
 * Busca dados agrupados do motor por base legal
 * Filtra apenas registros com compoeProjeto = true e baseLegalId não nulo
 * @param {number} versaoId - ID da versão
 * @param {string} script - Script do motor (ex: CONCILIACAO_SPED)
 * @returns {Promise<Object>} Dados agrupados por base legal
 */
export function buscarMotorAgrupadoBaseLegal(versaoId, script) {
  return apiPiscofins.get(
    `/versoes/${versaoId}/motores/${script}/agrupado-base-legal`
  );
}

/**
 * Busca dados da tela Base Legal por versão (rota dedicada: conciliacao_sped_detalhes).
 * @param {number} versaoId - ID da versão
 * @returns {Promise<Object>} { sucesso, linhas, totais, empresa, cnpjEmpresa, periodo }
 */
export function buscarBaseLegalPorVersao(versaoId) {
  return apiPiscofins.get(`/conciliacao_sped/base_legal/${versaoId}`);
}

/**
 * Atualiza detalhe de um lançamento na conciliação SPED (base_legal_id obrigatório; compoe deriva no backend).
 * @param {Object} body - { id_lancamento, id_versao, schema_empresa, base_legal_id }
 * @returns {Promise<Object>} { sucesso }
 */
export function atualizarDetalheLancamento(body) {
  return apiPiscofins.patch('/conciliacao_sped/detalhes/lancamento', body);
}

/**
 * Atualiza base_legal_id para uma conta (todos os lançamentos da conta no schema da prévia); compoe deriva no backend.
 * @param {string|number} contaId - Id da conta para a URL (id_conta_contabil)
 * @param {Object} body - { schema_empresa, id_conta_contabil, id_versao, base_legal_id }
 * @returns {Promise<Object>} { sucesso, linhasAtualizadas, totais? }
 */
export function atualizarDetalhesConta(contaId, body) {
  return apiPiscofins.patch(`/conciliacao_sped/detalhes/conta/${encodeURIComponent(contaId)}`, body);
}

/**
 * Busca dados detalhados do motor
 * @param {number} versaoId - ID da versão
 * @param {string} script - Script do motor
 * @param {number} pagina - Número da página (padrão: 1)
 * @param {number} limite - Limite por página (padrão: 10)
 * @param {boolean} semPaginacao - Se true, retorna todos os registros sem paginação
 * @param {string} filtroColuna - Coluna para filtrar (opcional)
 * @param {string|number|boolean|null} filtroValor - Valor do filtro (opcional)
 * @param {boolean} incluirTodos - Se true, retorna todos os registros sem filtrar por compoeProjeto (padrão: false)
 * @param {string} filtroDescricao - Filtro adicional por descrição de lançamento (opcional)
 * @param {string} filtroDataLancamento - Filtro adicional por data de lançamento YYYY-MM-DD (opcional)
 * @param {string} filtroDescricaoConta - Filtro adicional por descrição de conta (opcional)
 * @param {string} filtroCnpj - Filtro adicional por CNPJ (opcional)
 * @returns {Promise<Object>} Dados detalhados paginados ou todos os dados
 */
export function buscarMotorDetalhes(versaoId, script, pagina = 1, limite = 10, semPaginacao = false, filtroColuna = undefined, filtroValor = undefined, incluirTodos = false, filtroDescricao = undefined, filtroDataLancamento = undefined, filtroDescricaoConta = undefined, filtroCnpj = undefined) {
  const queryParams = new URLSearchParams();
  
  if (semPaginacao) {
    queryParams.append('semPaginacao', 'true');
  } else {
    if (pagina) queryParams.append('pagina', pagina.toString());
    if (limite) queryParams.append('limite', limite.toString());
  }
  
  // ✅ Novo parâmetro para incluir todos os registros (sem filtrar por compoeProjeto)
  if (incluirTodos) {
    queryParams.append('incluirTodos', 'true');
  }
  
  // ✅ Novo parâmetro: filtro de descrição (pode ser combinado com outros filtros)
  if (filtroDescricao && filtroDescricao.trim()) {
    queryParams.append('filtroDescricao', filtroDescricao.trim());
  }
  
  // ✅ Novos parâmetros: filtros adicionais de linha (podem ser combinados com filtro de conta)
  if (filtroDataLancamento && filtroDataLancamento.trim()) {
    queryParams.append('filtroDataLancamento', filtroDataLancamento.trim());
  }
  
  if (filtroDescricaoConta && filtroDescricaoConta.trim()) {
    queryParams.append('filtroDescricaoConta', filtroDescricaoConta.trim());
  }
  
  if (filtroCnpj && filtroCnpj.trim()) {
    queryParams.append('filtroCnpj', filtroCnpj.trim());
  }
  
  // Adicionar filtros se fornecidos
  if (filtroColuna && filtroValor !== undefined && filtroValor !== null && filtroValor !== '') {
    queryParams.append('filtroColuna', filtroColuna);
    
    // Converter valor para string apropriadamente
    if (typeof filtroValor === 'boolean') {
      queryParams.append('filtroValor', filtroValor ? 'true' : 'false');
    } else if (filtroValor === null) {
      queryParams.append('filtroValor', 'null');
    } else {
      queryParams.append('filtroValor', String(filtroValor));
    }
  }
  
  const queryString = queryParams.toString();
  const url = `/versoes/${versaoId}/motores/${script}${queryString ? `?${queryString}` : ''}`;
  
  return apiPiscofins.get(url);
}


/**
 * Marca resumo como true na versão da prévia e salva valores calculados
 * @param {number} versaoId - ID da versão
 * @param {Object} valores - { script, pis, cofins, total } (opcional)
 * @returns {Promise<Object>} Resultado da atualização
 */
export function marcarResumoVersaoPrevia(versaoId, valores = null) {
  const body = valores
    ? {
        resumo: true,
        valores: {
          script: valores.script || "CONCILIACAO_SPED",
          pis: valores.pis || 0,
          cofins: valores.cofins || 0,
          total: valores.total || (valores.pis || 0) + (valores.cofins || 0),
        },
      }
    : { resumo: true };

  return apiPiscofins.put(`/versoes/${versaoId}/marcar-resumo`, body);
}

/**
 * Busca valores salvos do resumo para todos os motores
 * @param {number} versaoId - ID da versão
 * @returns {Promise<Object>} Valores salvos por script
 */
export function buscarValoresResumo(versaoId) {
  return apiPiscofins.get(`/versoes/${versaoId}/resumo/valores`);
}

/**
 * Remove o status de uma versão do Redis.
 * Deve ser chamado quando a versão é concluída (sucesso ou erro).
 * A operação deve ser idempotente no backend.
 *
 * @param {number} versaoId - ID da versão
 * @returns {Promise<Object>} Resultado da remoção
 */
export function removerStatusVersao(versaoId) {
  return apiPiscofins.delete(`/versoes/${versaoId}/status`);
}

/**
 * Valida se todos os lançamentos com "Compõe Projeto" marcado têm "Base Legal" selecionada
 * Valida diretamente no MongoDB para garantir consistência
 * @param {number} versaoId - ID da versão
 * @param {string} script - Script do motor (ex: CONCILIACAO_SPED)
 * @returns {Promise<Object>} Resultado da validação { valido, mensagem, lancamentosSemBaseLegal, totalLancamentosComProjeto }
 */
export function validarBaseLegalCompoeProjeto(versaoId, script) {
  return apiPiscofins.get(
    `/versoes/${versaoId}/motores/${script}/validar-base-legal`
  );
}

// ==========================
// VERSÕES - Endpoints para gerenciamento de versões
// ==========================

/**
 * Cria uma nova versão copiando de uma versão existente
 * @param {number} idPrevia - ID da prévia
 * @param {Object} body - { versaoIdOriginal, tiposPreviaIds }
 * @returns {Promise<Object>} Dados da nova versão criada
 */
export function copiarVersaoPrevia(idPrevia, body) {
  return apiPiscofins.post(
    `/previas/pis-cofins/${idPrevia}/versoes/copiar`,
    body
  );
}

// ==========================
// PROJETOS - Endpoints para gerenciamento de projetos
// ==========================

/**
 * Inicia um projeto PIS/COFINS a partir de uma prévia
 * @param {number} idPrevia - ID da prévia
 * @param {number} idPreviaVersao - ID da versão da prévia
 * @returns {Promise<Object>} Dados do projeto criado
 */
export function iniciarProjeto(idPrevia, idPreviaVersao) {
  return apiPiscofins.post('/piscofins/projetos/iniciar', {
    idPrevia,
    idPreviaVersao,
  });
}
